import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import usePhotos from "../hooks/usePhotos";

const A = "https://customer-assets-0z36b82j.emergentagent.net/job_casale-tradizione/artifacts";

const PHOTOS = [
  {
    src: `${A}/043031d50d79b532_e429301f-f6dc-473c-acf1-4bebda3e8851.jpeg`,
    alt: "La sala principale con le volte in cotto e il banco",
    label: { it: "La Sala delle Volte", en: "The Vaulted Dining Room" },
  },
  {
    src: `${A}/6ac007e642e842e4_9dfca840-4b1c-4bc8-a0ed-9f8c57cc7734.jpeg`,
    alt: "La corte interna con gli ombrelli colorati illuminati",
    label: { it: "La Corte degli Ombrelli", en: "The Umbrella Courtyard" },
  },
  {
    src: `${A}/dfdd9df47f411d28_e655c322-b661-4900-964f-1c3ac4ec2fc4.jpeg`,
    alt: "Il tavolo del dehors tra il verde",
    label: { it: "Il Dehors", en: "The Terrace" },
  },
  {
    src: `${A}/f8050307fb69d97d_40e04667-8327-4da0-a4e5-76f29c74f1e8.jpeg`,
    alt: "L'angolo del bar con le grappe Francoli",
    label: null,
  },
  {
    src: `${A}/9b412e13937b38b2_2f3201b7-d5b6-44ff-83ab-09bb7376061f.jpeg`,
    alt: "La sala con il frigo dei vini e lo specchio antico",
    label: null,
  },
  {
    src: `${A}/3d45c594513ae9a2_ea486e79-e7a2-456c-a91e-5fdca454961d.jpeg`,
    alt: "La parete dei vini retroilluminata con i calici",
    label: { it: "La Parete dei Vini", en: "The Wine Wall" },
  },
  {
    src: `${A}/fb0a1bb85381734f_9d489aab-570c-4261-8bad-0ffba8e7040c.jpeg`,
    alt: "Le bottiglie orizzontali sulla parete in legno",
    label: { it: "Le Etichette", en: "The Labels" },
  },
  {
    src: `${A}/3258d096a58812bb_7162f804-faf0-4636-ae39-f94ea69bebe6.jpeg`,
    alt: "La saletta con le tende e la carta da parati",
    label: { it: "La Saletta Riservata", en: "The Private Room" },
  },
  {
    src: `${A}/583a837a4951a1af_2aa53f9e-6b37-4653-8c90-c438f46c1436.jpeg`,
    alt: "L'arco d'ingresso con la lanterna",
    label: { it: "L'Arco d'Ingresso", en: "The Entrance Arch" },
  },
  {
    src: `${A}/1ce4109a67125604_4ab0b553-7880-467a-9733-25d8d4012e61.jpeg`,
    alt: "La lampada delfino in vetro e i pavimenti d'epoca",
    label: { it: "I Dettagli", en: "The Details" },
  },
];

export default function Galleria() {
  const { lang } = useLang();
  const photos = usePhotos();
  return (
    <section id="galleria" data-testid="galleria-section" className="py-24 lg:py-32 bg-[#171512]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <span className="eyebrow">{lang === "it" ? "La Galleria" : "The Gallery"}</span>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF7F2]">
              {lang === "it" ? (
                <>Scorci di <span className="italic text-[#C9A265]">casa Milano</span></>
              ) : (
                <>Glimpses of <span className="italic text-[#C9A265]">Casa Milano</span></>
              )}
            </h2>
          </div>
          <p className="max-w-sm text-sm text-[#7A7267] leading-relaxed">
            {lang === "it"
              ? "Le volte in cotto, la corte, il dehors e la parete dei vini: la casa che vi aspetta in Piazza Castello, fotografata com'è."
              : "The brick vaults, the courtyard, the terrace and the wine wall: the home awaiting you in Piazza Castello, photographed as it is."}
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {PHOTOS.map((photo, i) => (
            <motion.figure
              key={photo.alt}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12 }}
              className={`group relative overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.06)] hover:border-[rgba(201,162,101,0.4)] transition-colors duration-500 ${
                i === 0 || i === 4 ? "row-span-2 h-80 lg:h-[520px]" : "h-40 lg:h-[250px]"
              }`}
            >
              <img
                src={photos[`gallery-${i}`] || photo.src}
                alt={photo.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.4s]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0A]/85 via-transparent to-transparent opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
              {photo.label && (
                <figcaption className="absolute bottom-4 left-5 font-serif-display italic text-lg text-[#FAF7F2] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                  {photo.label[lang]}
                </figcaption>
              )}
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
